/**
 * API Key Mutation Hooks
 *
 * React Query hooks for API key mutations (create, delete, revoke).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminApiKeyService } from '@/services/admin-api-key.service'
import { apiKeyKeys } from './useApiKeyQueries'
import type { CreateApiKeyRequest } from '@/types/api-keys.types'

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new API key
 *
 * Invalidates:
 * - All API key list queries
 */
export function useCreateApiKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateApiKeyRequest) => adminApiKeyService.createApiKey(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.lists() })
      toast.success('API key created successfully', {
        description: `${data.apiKey.name} has been created. Save the key now, you won't see it again.`,
      })
    },
    onError: (error: Error) => {
      toast.error('Failed to create API key', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete an API key
 *
 * Invalidates:
 * - All list queries
 */
export function useDeleteApiKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiKeyService.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.lists() })
      toast.success('API key deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete API key', {
        description: error.message,
      })
    },
  })
}

/**
 * Revoke an API key
 *
 * Invalidates:
 * - All list queries
 */
export function useRevokeApiKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adminApiKeyService.revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.lists() })
      toast.success('API key revoked successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to revoke API key', {
        description: error.message,
      })
    },
  })
}
